// Same-origin catalogue data loads only on demand; no tracking or persistent storage.
export const normalizeVendor = value => String(value).replace(/[®™]/g, '').trim().toLocaleLowerCase();
const normalizeText = value => String(value ?? '').normalize('NFKC').toLocaleLowerCase();
export function matchesGift(item, criteria) {
  if (!Number.isFinite(item.price) || item.price < 0) return false;
  if (criteria.occasion && !item.occasions.includes(criteria.occasion)) return false;
  if (criteria.budget && item.price > criteria.budget) return false;
  return !criteria.personal || item.personal;
}
export function selectCatalog(products, criteria, options) {
  const ownVendor = normalizeVendor(options.vendor);
  const requests = new Set(options.requests.map(String));
  return products.filter(item => {
    if (!options.otherVendors && normalizeVendor(item.vendor) !== ownVendor) return false;
    const explicit = Array.isArray(item.occasions) ? item.occasions : [];
    const fields = {tags: item.tags || [], title: [item.title], type: [item.type]};
    const text = normalizeText((fields[criteria.searchIn] || [item.title, item.type, ...(item.tags || [])]).join(' '));
    const occasionMatch = !criteria.occasion || criteria.collection === true || explicit.includes(criteria.occasion) ||
      criteria.keywords.some(word => word && text.includes(normalizeText(word)));
    return occasionMatch && matchesGift({...item, occasions: [criteria.occasion], personal: item.personal || requests.has(String(item.id))}, criteria);
  }).sort((a, b) => options.prioritize ? Number(normalizeVendor(b.vendor) === ownVendor) - Number(normalizeVendor(a.vendor) === ownVendor) : 0);
}
export async function loadCatalog(baseUrl, {signal, fetcher = globalThis.fetch} = {}) {
  const base = new URL(baseUrl);
  const fetchPage = async page => {
    const url = new URL(base);
    url.searchParams.set('section_id', 'mabe-gift-catalog');
    url.searchParams.set('page', String(page));
    // One bounded retry. A partial catalogue must never be presented as complete.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await fetcher(url, {signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(20000)]) : AbortSignal.timeout(20000), credentials:'same-origin'});
        if (!response.ok) throw new Error(`Catalogue HTTP ${response.status}`);
        const html = await response.text();
        const match = html.match(/<script[^>]*data-gift-catalog[^>]*>([\s\S]*?)<\/script>/);
        if (!match) throw new Error('Catalogue response missing');
        const payload = JSON.parse(match[1]);
        if (payload.page !== page || !Number.isInteger(payload.pages) || payload.pages < 1 || !Array.isArray(payload.products)) throw new Error('Invalid catalogue page');
        if (payload.pages > 100) throw new Error('Catalogue exceeds Shopify pagination limit');
        return payload;
      } catch (error) {
        if (signal?.aborted || attempt === 1) throw error;
      }
    }
  };
  const first = await fetchPage(1);
  const pages = [first];
  // At most three concurrent requests. Page order remains deterministic for ranking.
  for (let page = 2; page <= first.pages; page += 3) {
    const numbers = Array.from({length:Math.min(3, first.pages - page + 1)}, (_, i) => page + i);
    pages.push(...await Promise.all(numbers.map(fetchPage)));
  }
  const items = new Map();
  for (const page of pages) {
    if (page.pages !== first.pages) throw new Error('Catalogue changed while loading; retry');
    for (const item of page.products) items.set(String(item.id), item);
  }
  return [...items.values()];
}
class MabeGiftFinder extends (globalThis.HTMLElement ?? class {}) {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const {signal} = this.controller;
    const form = this.querySelector('form');
    const results = this.querySelector('[data-gift-results]');
    const status = this.querySelector('[data-gift-status]');
    const empty = this.querySelector('[data-gift-empty]');
    const loading = this.querySelector('[data-gift-loading]');
    const more = this.querySelector('[data-gift-more]');
    if (!form || !results || !status || !empty || !more) return;
    const items = [...this.querySelectorAll('[data-gift-item]')];
    const configured = [...this.querySelectorAll('[data-gift-occasion]')];
    if (configured.length) {
      // A single authoritative block list; legacy textarea remains a fallback for old instances.
      const any = form.elements.occasion.options[0];
      form.elements.occasion.replaceChildren(any);
      const keys = new Set();
      for (const block of configured) {
        if (keys.has(block.dataset.key)) continue;
        keys.add(block.dataset.key);
        const option = document.createElement('option');
        option.value = block.dataset.key; option.textContent = block.dataset.label;
        option.dataset.keywords = block.dataset.keywords;
        option.dataset.searchIn = block.dataset.searchIn;
        option.dataset.collection = block.dataset.collection;
        form.elements.occasion.append(option);
      }
    }
    this.catalogs ??= new Map();
    this.pendingCatalogs ??= new Map();
    let generation = 0;
    let matches = [];
    let shown = 0;
    const requests = JSON.parse(this.querySelector('[data-gift-request-products]').textContent);
    const append = () => {
      const fragment = document.createDocumentFragment();
      const end = Math.min(shown + (Number(this.dataset.pageSize) || 8), matches.length);
      for (const item of matches.slice(shown, end)) fragment.append(this.card(item, requests));
      results.append(fragment);
      shown = end;
      more.hidden = shown >= matches.length;
    };
    const reset = () => {
      generation++;
      results.hidden = true; empty.hidden = true; more.hidden = true;
      status.textContent = ''; results.removeAttribute('aria-busy'); loading.hidden = true;
    };
    reset();
    form.hidden = false;
    form.addEventListener('submit', async event => {
      event.preventDefault();
      const current = ++generation;
      const data = new FormData(form);
      const selected = form.elements.occasion.selectedOptions[0];
      const criteria = {collection: Boolean(selected.dataset.collection), searchIn: selected.dataset.searchIn || 'all', occasion: String(data.get('occasion') || ''), budget: Number(data.get('budget')) || 0, personal: data.get('personal') === 'yes', keywords: selected.dataset.keywords?.split(',').map(x => x.trim()).filter(Boolean) || []};
      empty.hidden = true; more.hidden = true;
      try {
        if (this.dataset.catalog === 'true') {
          results.hidden = true;
          results.setAttribute('aria-busy', 'true');
          status.textContent = this.dataset.loadingLabel;
          const url = new URL(selected.dataset.collection || this.dataset.catalogUrl, location.origin);
          if (url.origin !== location.origin) throw new Error('Catalogue must be same origin');
          loading.hidden = this.catalogs.has(url.href);
          if (!this.catalogs.has(url.href)) {
            if (!this.pendingCatalogs.has(url.href)) {
              this.pendingCatalogs.set(url.href, loadCatalog(url, {signal}).finally(() => this.pendingCatalogs.delete(url.href)));
            }
            const catalog = await this.pendingCatalogs.get(url.href);
            if (signal.aborted) return;
            this.catalogs.set(url.href, catalog);
          }
          if (current !== generation || signal.aborted) return;
          matches = selectCatalog(this.catalogs.get(url.href), criteria, {vendor:this.dataset.vendor, prioritize:this.dataset.prioritize === 'true', otherVendors:this.dataset.otherVendors === 'true', requests});
          results.replaceChildren(); shown = 0; append();
        } else {
          matches = items.filter(element => {
            const item = {price: element.dataset.price === '' ? NaN : Number(element.dataset.price), occasions: element.dataset.occasions.split(',').map(x => x.trim()).filter(Boolean), personal: element.dataset.personal === 'true'};
            const match = matchesGift(item, criteria); element.hidden = !match; return match;
          });
        }
        results.hidden = false; empty.hidden = matches.length !== 0;
        status.textContent = this.dataset.resultLabel.replace('{count}', String(matches.length));
        this.dispatchEvent(new CustomEvent('mabe:gift-results', {bubbles:true, detail:{count:matches.length}}));
      } catch (error) {
        if (current !== generation || signal.aborted) return;
        status.textContent = this.dataset.errorLabel;
        results.hidden = true;
        console.error('MaBe gift catalogue:', error);
      } finally {
        if (current === generation) {results.removeAttribute('aria-busy'); loading.hidden = true;}
      }
    }, {signal});
    more.addEventListener('click', () => {const before = shown; append(); results.children[before]?.querySelector('a')?.focus();}, {signal});
    form.addEventListener('reset', reset, {signal});
  }
  card(item, requests) {
    const make = (tag, className, text) => {const el = document.createElement(tag); el.className = className; if (text) el.textContent = text; return el;};
    const article = make('article', 'mabe-gift__catalog-card');
    const link = make('a', 'mabe-gift__catalog-image');
    const url = new URL(item.url, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid product link');
    link.href = url.href; link.setAttribute('aria-label', item.title);
    if (item.image) {const image = make('img', ''); image.src = item.image; image.alt = item.alt || item.title; image.width = 600; image.height = 600; image.loading = 'lazy'; image.decoding = 'async'; link.append(image);}
    const copy = make('div', 'mabe-gift__catalog-copy');
    copy.append(make('p', 'mabe-gift__catalog-vendor', item.vendor));
    const heading = make('h3', ''); const title = make('a', '', item.title); title.href = url.href; heading.append(title); copy.append(heading);
    copy.append(make('p', '', new Intl.NumberFormat(document.documentElement.lang || 'de', {style:'currency', currency:item.currency}).format(item.price / 100)));
    const product = make('a', 'mabe-gift__catalog-link', this.dataset.productLabel); product.href = url.href; copy.append(product);
    if (requests.map(String).includes(String(item.id)) && !item.personal) {
      const request = make('a', 'mabe-gift__catalog-link', this.dataset.requestLabel); request.href = this.dataset.requestLink || url.href; copy.append(request);
    }
    article.append(link, copy); return article;
  }
  disconnectedCallback() {this.controller?.abort(); this.controller = null; this.pendingCatalogs?.clear();}
}
if (globalThis.customElements && !customElements.get('mabe-gift-finder')) customElements.define('mabe-gift-finder', MabeGiftFinder);
